import { Check, X } from "lucide-react";

const LABELS = ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"];
const SEGMENT_COLORS = [
  "bg-red-500",
  "bg-orange-500",
  "bg-yellow-500",
  "bg-lime-500",
  "bg-green-500",
];
const LABEL_COLORS = [
  "text-stone-400",
  "text-red-600",
  "text-orange-600",
  "text-yellow-600",
  "text-lime-700",
  "text-green-700",
];

export function evaluatePassword(pw) {
  const checks = {
    length: pw.length >= 8,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  const classCount = ["lower", "upper", "digit", "symbol"].reduce(
    (n, k) => n + (checks[k] ? 1 : 0),
    0,
  );
  // 1 point for length, 1 per character class (max 5)
  const score = (checks.length ? 1 : 0) + classCount;
  // Policy: 8+ chars AND >=3 of 4 classes
  const meetsPolicy = checks.length && classCount >= 3;
  return { score, checks, classCount, meetsPolicy };
}

/**
 * Visual strength meter + requirement checklist.
 * Renders nothing when `password` is empty so it doesn't push layout on focus.
 */
export default function PasswordStrength({ password = "", testidPrefix = "password-strength" }) {
  if (!password) return null;
  const { score, checks, meetsPolicy } = evaluatePassword(password);

  const requirements = [
    { key: "length", label: "At least 8 characters", met: checks.length },
    { key: "lower", label: "Lowercase letter (a–z)", met: checks.lower },
    { key: "upper", label: "Uppercase letter (A–Z)", met: checks.upper },
    { key: "digit", label: "Digit (0–9)", met: checks.digit },
    { key: "symbol", label: "Symbol (e.g. !@#$%)", met: checks.symbol },
  ];

  return (
    <div className="mt-2 space-y-2" data-testid={testidPrefix}>
      <div className="flex gap-1" data-testid={`${testidPrefix}-bar`}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i < score ? SEGMENT_COLORS[Math.min(score, 5) - 1] : "bg-stone-200"
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span
          className={`font-semibold ${LABEL_COLORS[score]}`}
          data-testid={`${testidPrefix}-label`}
        >
          {LABELS[score]}
        </span>
        <span className="text-stone-500">
          Need 8+ chars and 3 of 4 character types
        </span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid={`${testidPrefix}-checklist`}>
        {requirements.map((r) => (
          <li
            key={r.key}
            className={`flex items-center gap-1.5 ${
              r.met ? "text-green-700" : "text-stone-500"
            }`}
            data-testid={`${testidPrefix}-req-${r.key}-${r.met ? "met" : "unmet"}`}
          >
            {r.met ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <X className="w-3.5 h-3.5 text-stone-400" />
            )}
            <span>{r.label}</span>
          </li>
        ))}
      </ul>
      {!meetsPolicy && (
        <p className="text-[11px] text-stone-500" data-testid={`${testidPrefix}-policy-note`}>
          Your password doesn't meet the minimum requirements yet.
        </p>
      )}
    </div>
  );
}
